import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import { ClipboardCheck, Plus, Search, Calendar, Eye, Trash2, Play, Check, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Project {
  _id: string;
  name: string;
}

interface Unit {
  _id: string;
  unitNo: string;
}

interface Checklist {
  _id: string;
  name: string;
  category: string;
}

interface User {
  contactId: string;
  name: string;
  email: string;
  construction: boolean;
  inspector: boolean;
  manager: boolean;
  trade: boolean;
}

interface Inspection {
  _id: string;
  unit: {
    _id: string;
    unitNo: string;
  };
  checklistName: string;
  inspectionTypeName: string;
  inspector: {
    contactId?: string;
    name: string;
    email: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  scheduledDate: string;
  startTime?: string;
  endTime?: string;
  passedCount: number;
  failedCount: number;
  naCount: number;
  createdAt: string;
}

const Inspection: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [loadingInspections, setLoadingInspections] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    scheduled: 0,
    'in-progress': 0,
    completed: 0,
    cancelled: 0
  });

  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState<string | null>(null);
  const [schedulingInProgress, setSchedulingInProgress] = useState(false);
  const [deletingInProgress, setDeletingInProgress] = useState(false);
  const [units, setUnits] = useState<Unit[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [inspectors, setInspectors] = useState<User[]>([]);
  const [scheduleForm, setScheduleForm] = useState({
    unitId: '',
    checklistId: '',
    inspectorId: '',
    inspectorName: '',
    inspectorEmail: '',
    scheduledDate: ''
  });
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const unitDropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
        setShowUnitDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up timeout on unmount
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (selectedProjectId) {
      loadInspections();
      loadCounts();
    }
  }, [selectedProjectId, selectedStatus, searchQuery]);

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project
      if (projectsList.length > 0 && !selectedProjectId) {
        setSelectedProjectId(projectsList[0]._id);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  };

  const loadInspections = async () => {
    if (!selectedProjectId) return;

    setLoadingInspections(true);
    try {
      const params: any = {};
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get(`/workspaces/${workspaceId}/${selectedProjectId}/inspections`, { params });
      setInspections(response.data.inspections || []);
    } catch (err: any) {
      console.error('Error loading inspections:', err);
      toast.error('Failed to load inspections');
    } finally {
      setLoadingInspections(false);
    }
  };

  const loadCounts = async () => {
    if (!selectedProjectId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/${selectedProjectId}/inspections/counts`);
      setStatusCounts(response.data.counts || {
        all: 0,
        scheduled: 0,
        'in-progress': 0,
        completed: 0,
        cancelled: 0
      });
    } catch (err: any) {
      console.error('Error loading counts:', err);
    }
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleScheduleInspection = async () => {
    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }

    // Load checklists and inspectors (users with inspector role)
    try {
      const [checklistsRes, usersRes] = await Promise.all([
        api.get(`/checklists/${selectedProjectId}`, {
          params: { limit: 1000 }
        }),
        api.get(`/service-settings/${selectedProjectId}/users`)
      ]);

      setChecklists(checklistsRes.data.checklists || []);
      // Filter users with inspector role
      const inspectorUsers = (usersRes.data.users || []).filter((user: User) => user.inspector);
      setInspectors(inspectorUsers);
      setShowScheduleModal(true);
    } catch (err: any) {
      console.error('Error loading form data:', err);
      toast.error('Failed to load form data');
    }
  };

  const handleScheduleFormChange = (field: string, value: string) => {
    if (field === 'inspectorId') {
      const inspector = inspectors.find(u => u.contactId === value);
      setScheduleForm({
        ...scheduleForm,
        inspectorId: value,
        inspectorName: inspector?.name || '',
        inspectorEmail: inspector?.email || ''
      });
    } else {
      setScheduleForm({ ...scheduleForm, [field]: value });
    }
  };

  const handleUnitSelect = (unitId: string) => {
    setScheduleForm({ ...scheduleForm, unitId });
    const selectedUnit = units.find(u => u._id === unitId);
    setUnitSearchQuery(selectedUnit?.unitNo || '');
    setShowUnitDropdown(false);
  };

  const handleUnitSearchChange = (value: string) => {
    setUnitSearchQuery(value);
    setScheduleForm({ ...scheduleForm, unitId: '' });
    setShowUnitDropdown(true);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce API call - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(async () => {
      // Fetch units from API when search query changes
      if (value.trim() && selectedProjectId) {
        try {
          const response = await api.get(`/workspaces/${workspaceId}/units/by-project/${selectedProjectId}`, {
            params: {
              search: value,
              limit: 50,
              sortBy: 'unitNo',
              sortOrder: 'asc'
            }
          });
          setUnits(response.data.units || []);
        } catch (err: any) {
          console.error('Error searching units:', err);
        }
      } else if (!value.trim() && selectedProjectId) {
        // Load all units when search is cleared
        try {
          const response = await api.get(`/workspaces/${workspaceId}/units/by-project/${selectedProjectId}`, {
            params: {
              limit: 1000,
              sortBy: 'unitNo',
              sortOrder: 'asc'
            }
          });
          setUnits(response.data.units || []);
        } catch (err: any) {
          console.error('Error loading units:', err);
        }
      }
    }, 300);
  };

  const filteredUnits = units;

  const handleScheduleSubmit = async () => {
    if (!scheduleForm.unitId || !scheduleForm.checklistId || !scheduleForm.inspectorName || !scheduleForm.scheduledDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSchedulingInProgress(true);
      await api.post(`/workspaces/${workspaceId}/${selectedProjectId}/inspections`, scheduleForm);
      toast.success('Inspection scheduled successfully');
      setShowScheduleModal(false);
      setScheduleForm({
        unitId: '',
        checklistId: '',
        inspectorId: '',
        inspectorName: '',
        inspectorEmail: '',
        scheduledDate: ''
      });
      setUnitSearchQuery('');
      loadInspections();
      loadCounts();
    } catch (err: any) {
      console.error('Error scheduling inspection:', err);
      toast.error(err.response?.data?.message || 'Failed to schedule inspection');
    } finally {
      setSchedulingInProgress(false);
    }
  };

  const handleStartInspection = async (inspectionId: string) => {
    try {
      await api.post(`/workspaces/${workspaceId}/${selectedProjectId}/inspections/${inspectionId}/start`);
      toast.success('Inspection started');
      loadInspections();
      loadCounts();
      // Navigate to walkthrough page with project ID in state
      navigate(`/workspaces/${workspaceId}/inspection/${inspectionId}/walkthrough`, {
        state: { projectId: selectedProjectId }
      });
    } catch (err: any) {
      console.error('Error starting inspection:', err);
      toast.error(err.response?.data?.message || 'Failed to start inspection');
    }
  };

  const handleViewInspection = (inspectionId: string) => {
    navigate(`/workspaces/${workspaceId}/inspection/${inspectionId}/walkthrough`, {
      state: { projectId: selectedProjectId }
    });
  };

  const handleDeleteClick = (inspectionId: string) => {
    setInspectionToDelete(inspectionId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inspectionToDelete) return;

    setDeletingInProgress(true);

    try {
      await api.delete(`/workspaces/${workspaceId}/${selectedProjectId}/inspections/${inspectionToDelete}`);
      toast.success('Inspection deleted successfully');
      setShowDeleteConfirm(false);
      setInspectionToDelete(null);
      setDeletingInProgress(false);
      loadInspections();
      loadCounts();
    } catch (err: any) {
      console.error('Error deleting inspection:', err);
      toast.error(err.response?.data?.message || 'Failed to delete inspection');
      setDeletingInProgress(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return '#48182F';
      case 'in-progress':
        return '#F59E0B';
      case 'completed':
        return '#10B981';
      case 'cancelled':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Page Header with Project Dropdown */}
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h1 className="builders-section-title">Inspection</h1>
              <p className="builders-section-subtitle">
                Schedule and manage property inspections
              </p>
            </div>
            <div style={{ marginTop: '4px' }}>
              <select
                className="form-input"
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                disabled={loading || projects.length === 0}
                style={{ minWidth: '280px' }}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : !selectedProjectId ? (
            <div className="builders-empty-state">
              <p>Please select a project to manage inspections</p>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              {/* Status Tabs */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '20px',
                backgroundColor: '#D4CBC4',
                padding: '10px',
                borderRadius: '8px',
                overflowX: 'auto'
              }}>
                {[
                  { key: 'all', label: 'All' },
                  { key: 'scheduled', label: 'Scheduled' },
                  { key: 'in-progress', label: 'In Progress' },
                  { key: 'completed', label: 'Completed' },
                  { key: 'cancelled', label: 'Cancelled' }
                ].map((status) => (
                  <button
                    key={status.key}
                    onClick={() => setSelectedStatus(status.key)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '20px',
                      border: 'none',
                      backgroundColor: selectedStatus === status.key ? '#48182F' : '#FFFFFF',
                      color: selectedStatus === status.key ? 'white' : '#6B7280',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {status.label}
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: selectedStatus === status.key ? 'rgba(255, 255, 255, 0.2)' : '#F3F4F6',
                      color: selectedStatus === status.key ? 'white' : '#6B7280'
                    }}>
                      {statusCounts[status.key as keyof typeof statusCounts]}
                    </span>
                  </button>
                ))}
              </div>

              {/* Search and Schedule Button */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9CA3AF'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search inspections..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="form-input"
                    style={{
                      width: '100%',
                      paddingLeft: '40px'
                    }}
                  />
                </div>
                <button className="btn btn-primary btn-md" onClick={handleScheduleInspection}>
                  <Calendar size={20} />
                  Schedule Inspection
                </button>
              </div>

              {/* Inspections Table */}
              {loadingInspections ? (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Unit</th>
                        <th>Checklist</th>
                        <th>Inspector</th>
                        <th>Scheduled Date</th>
                        <th>Status</th>
                        <th>Results</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(5)].map((_, index) => (
                        <tr key={index}>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '80px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '150px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '120px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '100px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '24px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '12px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '90px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '80px'
                            }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <style>
                    {`
                      @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                      }
                    `}
                  </style>
                </div>
              ) : inspections.length === 0 ? (
                <div className="data-table-container">
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6B7280'
                  }}>
                    <ClipboardCheck size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      No inspections found
                    </p>
                    <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                      Get started by scheduling your first inspection
                    </p>
                    <button className="btn btn-primary btn-md" onClick={handleScheduleInspection}>
                      <Calendar size={20} />
                      Schedule Inspection
                    </button>
                  </div>
                </div>
              ) : (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Unit</th>
                        <th>Checklist</th>
                        <th>Inspector</th>
                        <th>Scheduled Date</th>
                        <th>Status</th>
                        <th>Results</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inspections.map((inspection) => (
                        <tr key={inspection._id}>
                          <td className="data-table-primary-cell">
                            {inspection.unit.unitNo}
                          </td>
                          <td>
                            <div style={{ fontWeight: '500', color: '#48182F' }}>{inspection.checklistName}</div>
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {inspection.inspectionTypeName}
                            </div>
                          </td>
                          <td>
                            <div>{inspection.inspector.name}</div>
                            {inspection.inspector.email && (
                              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                {inspection.inspector.email}
                              </div>
                            )}
                          </td>
                          <td className="data-table-secondary-cell">
                            {formatDate(inspection.scheduledDate)}
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 12px',
                              borderRadius: '16px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: `${getStatusBadgeColor(inspection.status)}15`,
                              color: getStatusBadgeColor(inspection.status)
                            }}>
                              {inspection.status === 'in-progress' ? 'In Progress' : inspection.status.charAt(0).toUpperCase() + inspection.status.slice(1)}
                            </span>
                          </td>
                          <td>
                            {inspection.status === 'completed' || inspection.status === 'in-progress' ? (
                              <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                                <span style={{ color: '#10B981' }}>
                                  <Check size={14} style={{ display: 'inline', marginRight: '4px' }} />
                                  {inspection.passedCount}
                                </span>
                                <span style={{ color: '#EF4444' }}>
                                  <X size={14} style={{ display: 'inline', marginRight: '4px' }} />
                                  {inspection.failedCount}
                                </span>
                                <span style={{ color: '#6B7280' }}>
                                  N/A: {inspection.naCount}
                                </span>
                              </div>
                            ) : (
                              <span style={{ color: '#6B7280', fontSize: '12px' }}>-</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                              {inspection.status === 'scheduled' && (
                                <button
                                  onClick={() => handleStartInspection(inspection._id)}
                                  className="btn btn-icon btn-sm"
                                  title="Start Inspection"
                                  style={{
                                    padding: '6px',
                                    color: '#48182F',
                                    border: 'none',
                                    background: 'transparent'
                                  }}
                                >
                                  <Play size={18} />
                                </button>
                              )}
                              {(inspection.status === 'in-progress' || inspection.status === 'completed') && (
                                <button
                                  onClick={() => handleViewInspection(inspection._id)}
                                  className="btn btn-icon btn-sm"
                                  title="View Inspection"
                                  style={{
                                    padding: '6px',
                                    color: '#48182F',
                                    border: 'none',
                                    background: 'transparent'
                                  }}
                                >
                                  <Eye size={18} />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteClick(inspection._id)}
                                className="btn btn-icon btn-sm"
                                title="Delete Inspection"
                                style={{
                                  padding: '6px',
                                  color: '#DC2626',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Schedule Inspection Modal */}
        {showScheduleModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                padding: '24px',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
                  Schedule Inspection
                </h2>
              </div>

              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '20px', position: 'relative' }} ref={unitDropdownRef}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Unit <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search unit..."
                      value={unitSearchQuery}
                      onChange={(e) => handleUnitSearchChange(e.target.value)}
                      onFocus={() => setShowUnitDropdown(true)}
                      autoComplete="off"
                    />
                    <Search
                      size={18}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9CA3AF',
                        pointerEvents: 'none'
                      }}
                    />
                  </div>
                  {showUnitDropdown && filteredUnits.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      marginTop: '4px'
                    }}>
                      {filteredUnits.map((unit) => (
                        <div
                          key={unit._id}
                          onClick={() => handleUnitSelect(unit._id)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#111827',
                            borderBottom: '1px solid #F3F4F6',
                            backgroundColor: scheduleForm.unitId === unit._id ? '#F3F4F6' : 'transparent',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F9FAFB';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = scheduleForm.unitId === unit._id ? '#F3F4F6' : 'transparent';
                          }}
                        >
                          {unit.unitNo}
                        </div>
                      ))}
                    </div>
                  )}
                  {showUnitDropdown && unitSearchQuery && filteredUnits.length === 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      marginTop: '4px',
                      textAlign: 'center',
                      color: '#6B7280',
                      fontSize: '14px'
                    }}>
                      No units found
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Checklist <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <select
                    className="form-input"
                    value={scheduleForm.checklistId}
                    onChange={(e) => handleScheduleFormChange('checklistId', e.target.value)}
                  >
                    <option value="">Select Checklist</option>
                    {checklists.map((checklist) => (
                      <option key={checklist._id} value={checklist._id}>
                        {checklist.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Inspector <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <select
                    className="form-input"
                    value={scheduleForm.inspectorId}
                    onChange={(e) => handleScheduleFormChange('inspectorId', e.target.value)}
                  >
                    <option value="">Select Inspector</option>
                    {inspectors.map((inspector) => (
                      <option key={inspector.contactId} value={inspector.contactId}>
                        {inspector.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Scheduled Date <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={scheduleForm.scheduledDate}
                    onChange={(e) => handleScheduleFormChange('scheduledDate', e.target.value)}
                  />
                </div>
              </div>

              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduleForm({
                      unitId: '',
                      checklistId: '',
                      inspectorId: '',
                      inspectorName: '',
                      inspectorEmail: '',
                      scheduledDate: ''
                    });
                    setUnitSearchQuery('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-md"
                  onClick={handleScheduleSubmit}
                  disabled={schedulingInProgress}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {schedulingInProgress ? 'Scheduling...' : 'Schedule'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Inspection"
          message="Are you sure you want to delete this inspection? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
          loading={deletingInProgress}
          onConfirm={handleDeleteConfirm}
          onClose={() => {
            if (!deletingInProgress) {
              setShowDeleteConfirm(false);
              setInspectionToDelete(null);
            }
          }}
        />
      </>
    </AppLayout>
  );
};

export default Inspection;
